import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import * as adminApi from '../../api/admin';
import type { PhysicalDevice } from '../../api/admin';
import { extractErrorMessage } from '../../api/errors';
import { RefreshButton } from '../../components/RefreshButton';
import { SortHeader } from '../../components/SortHeader';
import { sortRows, useTableSort } from '../../util/tableSort';
import { timeAgo, isOnline } from '../../util/deviceHealth';

/** "android 16" → "Android 16"; joins os_name + os_version. */
function prettyOs(d: PhysicalDevice): string {
  if (!d.os_name) return '—';
  const name = d.os_name.charAt(0).toUpperCase() + d.os_name.slice(1);
  return `${name} ${d.os_version ?? ''}`.trim();
}

type Status = adminApi.PhysicalDeviceEnrollment['status'];

function statusColor(status: Status): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'lost':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * One flat row = one tenant enrollment of one physical tablet. A tablet
 * enrolled in two tenants contributes two rows sharing the same `hardware_id`
 * and `app_version`, so sorting by Device ID groups them together and the
 * divergence (or agreement) in per-tenant labels/status is visible at a glance.
 */
interface Row {
  key: string;
  tenant_name: string;
  tenant_slug: string;
  label: string;
  make: string;
  hardware_id: string;
  status: Status;
  app_version: string | null;
  os: string;
  last_seen_at: string | null;
}

/**
 * Cross-tenant physical-device view (PHYSICAL-DEVICE-IDENTITY, superadmin).
 *
 * A single sortable table over every tenant enrollment of every physical
 * tablet. The `app_version` is authoritative (one per physical device, from
 * the central registry), so this is the direct answer to "the same device
 * shows up in tenant A and tenant B with different version numbers" — sort by
 * Device ID and the same tablet's rows sit together with one shared version.
 *
 * Devices enrolled before the identity feature (no hardware_id reported yet)
 * don't appear until they self-register on their next check-in.
 *
 * Rendered embedded as the "Devices" tab of the Operations page.
 */
export function PhysicalDevices({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const [devices, setDevices] = useState<PhysicalDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const sort = useTableSort<
    'tenant' | 'label' | 'make' | 'hardware' | 'status' | 'app' | 'os' | 'last_seen'
  >('hardware');

  const reload = async () => {
    setError(null);
    setRefreshing(true);
    try {
      setDevices(await adminApi.listPhysicalDevices());
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load physical devices'));
      setDevices([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  // Flatten the grouped payload into one row per tenant enrollment. A registry
  // row with no matching tenant device (shouldn't normally happen) still gets
  // a row so nothing silently disappears.
  const rows = useMemo<Row[]>(() => {
    if (!devices) return [];
    const out: Row[] = [];
    for (const d of devices) {
      const make = [d.make, d.model].filter(Boolean).join(' ') || '—';
      if (d.enrollments.length === 0) {
        out.push({
          key: d.hardware_id,
          tenant_name: '—',
          tenant_slug: '',
          label: '—',
          make,
          hardware_id: d.hardware_id,
          status: 'retired',
          app_version: d.app_version,
          os: prettyOs(d),
          last_seen_at: d.last_seen_at,
        });
        continue;
      }
      for (const e of d.enrollments) {
        out.push({
          key: `${d.hardware_id}:${e.device_id}`,
          tenant_name: e.tenant_name,
          tenant_slug: e.tenant_slug,
          label: e.label,
          make,
          hardware_id: d.hardware_id,
          status: e.status,
          app_version: d.app_version,
          os: prettyOs(d),
          last_seen_at: e.last_seen_at,
        });
      }
    }
    return out;
  }, [devices]);

  const sortedRows = useMemo(
    () =>
      sortRows(
        rows,
        (r) => {
          switch (sort.orderBy) {
            case 'tenant':
              return r.tenant_name;
            case 'label':
              return r.label;
            case 'make':
              return r.make;
            case 'hardware':
              return r.hardware_id;
            case 'status':
              return r.status;
            case 'app':
              return r.app_version;
            case 'os':
              return r.os;
            case 'last_seen':
              return r.last_seen_at;
          }
        },
        sort.order,
      ),
    [rows, sort.orderBy, sort.order],
  );

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack>
          {!embedded && <Typography variant="h4">Physical devices</Typography>}
          <Typography variant="body2" color="text.secondary">
            Every capture tablet by its hardware identity, one row per tenant
            it's enrolled in. Sort by Device ID to group a tablet's tenants —
            the app version is authoritative (one per physical device).
          </Typography>
        </Stack>
        <RefreshButton onClick={() => void reload()} refreshing={refreshing} />
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {devices === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No physical devices registered yet. A tablet appears here once it
            checks in from a capture-app build that reports its hardware
            identity.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <SortHeader label="Tenant" col="tenant" sort={sort} />
                <SortHeader label="Device label" col="label" sort={sort} />
                <SortHeader label="Make" col="make" sort={sort} />
                <SortHeader label="Device ID" col="hardware" sort={sort} />
                <SortHeader label="Status" col="status" sort={sort} />
                <SortHeader label="App" col="app" sort={sort} />
                <SortHeader label="OS" col="os" sort={sort} />
                <SortHeader label="Last seen" col="last_seen" sort={sort} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((r) => (
                <TableRow key={r.key} hover>
                  <TableCell>
                    <Typography variant="body2">{r.tenant_name}</Typography>
                    {r.tenant_slug && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {r.tenant_slug}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell>{r.make}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {r.hardware_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusColor(r.status)}
                      label={r.status}
                      variant={r.status === 'retired' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {r.app_version ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.os}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: isOnline(r.last_seen_at)
                            ? 'success.main'
                            : 'text.disabled',
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {timeAgo(r.last_seen_at)}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
